import { type VFC } from 'react'
import { StaticRouter } from 'react-router-dom/server'
import { type SimpleWorkspace } from '@opallabs/concepts'
import { type Theme } from '@opallabs/gem'
import { DefaultQueryClientProvider } from '@opallabs/higgs'
import { ThemeProvider, WorkspaceContext } from '@opallabs/hooks'
import { ResourceShareModal } from '@opallabs/permissions'
import { useReadContentAccess } from './queries/useReadContentAccess'
import { useUpdateContentAccess } from './queries/useUpdateContentAccess'

type ContentShareModalProps = {
  contentId: number | string
  contentTitle: string
  contentUuid: string
  momentId: number | string
  open: boolean
  onClose: () => void
  ownerUuid?: string
  readOnly?: boolean
}

const ContentShareModal: VFC<ContentShareModalProps> = ({
  contentId,
  contentTitle,
  contentUuid,
  momentId,
  open,
  onClose,
  ownerUuid,
  readOnly = false,
}) => {
  const contentAccess = useReadContentAccess({
    contentUuid,
    enabled: open,
    // Refetch on open so the modal reflects latest permissions
    refetchOnMount: 'always',
  })

  const updateContentAccess = useUpdateContentAccess({
    contentUuid,
  })

  return (
    <ResourceShareModal
      effectivePrivacy={null}
      isVisible={open}
      onClose={onClose}
      metadataQuery={contentAccess}
      onChangeAccess={async (access) => {
        await updateContentAccess.mutateAsync({ access })
      }}
      ownerId={ownerUuid}
      readOnly={readOnly}
      resourceId={contentUuid}
      resourceShareParams={{
        legacyContentId: String(contentId),
        momentId: String(momentId),
      }}
      resourceTitle={contentTitle}
      resourceType="content"
    />
  )
}

export const QueryableContentShareModal: VFC<
  ContentShareModalProps & { workspace: SimpleWorkspace; theme?: Theme }
> = ({ workspace, theme = 'dark', ...props }) => {
  const location = window.top?.location ?? window.location
  return (
    <DefaultQueryClientProvider>
      <StaticRouter location={location.pathname}>
        <WorkspaceContext.Provider value={workspace}>
          <ThemeProvider theme={theme}>
            <ContentShareModal {...props} />
          </ThemeProvider>
        </WorkspaceContext.Provider>
      </StaticRouter>
    </DefaultQueryClientProvider>
  )
}
